import { makeAutoObservable, runInAction } from 'mobx';
import { hashCapabilities } from './hashCapabilities';
import type { KeyValueStore } from './storage';
import type { DelegationApproval, ApprovalLookup, SaveApprovalInput, IssuedDelegation } from './types';

export interface DelegationsStoreOptions {
  storage: KeyValueStore;
  /**
   * Returns the current user's Cere SS58 address — namespaces all storage
   * keys for multi-user support. Read through a closure because at provider
   * mount time the user is `null`/anonymous and only becomes known after
   * login.
   */
  getWalletUserAddress: () => string;
}

const APPROVALS_KEY = (user: string) => `scp-wallet-v2:approvals:${user}`;
const HISTORY_KEY = (user: string) => `scp-wallet-v2:history:${user}`;

/**
 * Local approvals + history store for delegations. Spec §4.3.
 *
 * - **Approvals**: "remember this app for these capabilities" rules. A matching
 *   approval lets the popup skip the consent screen on subsequent requests.
 * - **History**: locally-cached records of delegations the user has issued, for
 *   display on the `/delegations` page. Filtered automatically by expiry.
 *
 * Storage is pluggable; production passes in a `window.localStorage` adapter
 * (`browserLocalStorage()`), tests pass in an in-memory fake.
 *
 * The in-memory cache is keyed by the user-address closure's last-seen value.
 * When the closure returns a different address (e.g. `anonymous` → real SS58
 * after login), the cache is transparently re-loaded from the new namespace
 * on the next read/write.
 */
export class DelegationsStore {
  private _approvals: DelegationApproval[] = [];
  private _issued: IssuedDelegation[] = [];
  private _cachedUser: string | null = null;

  constructor(private readonly opts: DelegationsStoreOptions) {
    this.syncCacheToCurrentUser();
    makeAutoObservable(this, {}, { autoBind: true });
  }

  /**
   * If the user-address closure now returns a different namespace than the
   * one we last loaded for, drop the in-memory cache and re-hydrate from
   * storage under the new namespace.
   */
  private syncCacheToCurrentUser(): void {
    const user = this.opts.getWalletUserAddress();
    if (this._cachedUser === user) return;
    this._cachedUser = user;
    this._approvals = this.loadApprovalsFor(user);
    this._issued = this.loadIssuedFor(user);
  }

  hashCapabilities(caps: string[]): string {
    return hashCapabilities(caps);
  }

  // Approvals

  listApprovals(): DelegationApproval[] {
    this.syncCacheToCurrentUser();
    return [...this._approvals];
  }

  findApproval(criteria: ApprovalLookup): DelegationApproval | null {
    this.syncCacheToCurrentUser();
    const hash = hashCapabilities(criteria.capabilities);
    return (
      this._approvals.find(
        (a) =>
          a.hostOrigin === criteria.hostOrigin &&
          a.capabilitiesHash === hash &&
          a.ttlCapSeconds >= criteria.ttlCapSeconds &&
          (criteria.agentPubkey ?? null) === (a.agentPubkey ?? null),
      ) ?? null
    );
  }

  saveApproval(input: SaveApprovalInput): DelegationApproval {
    this.syncCacheToCurrentUser();
    const approval: DelegationApproval = {
      hostOrigin: input.hostOrigin,
      capabilitiesHash: hashCapabilities(input.capabilities),
      ttlCapSeconds: input.ttlCapSeconds,
      approvedAtMs: Date.now(),
      agentPubkey: input.agentPubkey,
      scopeFingerprint: input.scopeFingerprint,
    };
    runInAction(() => {
      this._approvals = this._approvals.filter(
        (a) =>
          !(
            a.hostOrigin === approval.hostOrigin &&
            a.capabilitiesHash === approval.capabilitiesHash &&
            (a.agentPubkey ?? null) === (approval.agentPubkey ?? null)
          ),
      );
      this._approvals.push(approval);
      this.persistApprovals();
    });
    return approval;
  }

  revokeApproval(approval: DelegationApproval): void {
    this.syncCacheToCurrentUser();
    runInAction(() => {
      this._approvals = this._approvals.filter(
        (a) =>
          !(
            a.hostOrigin === approval.hostOrigin &&
            a.capabilitiesHash === approval.capabilitiesHash &&
            (a.agentPubkey ?? null) === (approval.agentPubkey ?? null)
          ),
      );
      this.persistApprovals();
    });
  }

  // History

  listIssued(): IssuedDelegation[] {
    this.syncCacheToCurrentUser();
    const now = Date.now();
    return this._issued.filter((d) => d.expiresAtMs > now);
  }

  recordIssued(input: Omit<IssuedDelegation, 'issuedAtMs'>): IssuedDelegation {
    this.syncCacheToCurrentUser();
    const entry: IssuedDelegation = { ...input, issuedAtMs: Date.now() };
    runInAction(() => {
      this._issued.push(entry);
      this.persistIssued();
    });
    return entry;
  }

  forgetIssued(token: string): void {
    this.syncCacheToCurrentUser();
    runInAction(() => {
      this._issued = this._issued.filter((d) => d.token !== token);
      this.persistIssued();
    });
  }

  private loadApprovalsFor(user: string): DelegationApproval[] {
    const raw = this.opts.storage.getItem(APPROVALS_KEY(user));
    if (!raw) return [];
    try {
      return JSON.parse(raw) as DelegationApproval[];
    } catch {
      return [];
    }
  }

  private loadIssuedFor(user: string): IssuedDelegation[] {
    const raw = this.opts.storage.getItem(HISTORY_KEY(user));
    if (!raw) return [];
    try {
      return JSON.parse(raw) as IssuedDelegation[];
    } catch {
      return [];
    }
  }

  private persistApprovals(): void {
    // Always persist under the *current* user namespace, not whichever
    // namespace happened to be active at construction time.
    const user = this.opts.getWalletUserAddress();
    this.opts.storage.setItem(APPROVALS_KEY(user), JSON.stringify(this._approvals));
  }

  private persistIssued(): void {
    const user = this.opts.getWalletUserAddress();
    this.opts.storage.setItem(HISTORY_KEY(user), JSON.stringify(this._issued));
  }
}
